import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { compose, withState } from "recompose";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Form, withOverlay } from "@dpdgroupuk/mydpd-ui";
import queryString from "query-string";
import { Field, formValueSelector, reduxForm, SubmissionError } from "redux-form";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { trackProps, withTrack, withTrackProps } from "@dpdgroupuk/react-event-tracker";
import { ROUTE, FORM, ANALYTICS, M } from "../../../../constants";
import { LinkArrowIcon } from "../../../../components/Icon";
import FormError from "../../../../components/FormError";
import { AuthHelpers, AuthErrors, AppActions } from "../../../../features";
import styles from "./LoginForm.module.scss";
import { withAppContext, withUrlResolver } from "../../../../Context";
import { StringUtil, PromiseUtil } from "../../../../utils";

const LoginForm = ({
  error,
  urlResolver,
  isWrongUrlError,
  handleSubmit,
  onLogin,
  pristine,
  submitting,
  username,
  password,
  onPasswordToggle,
  onChangePassword,
  onInputChange
}) => {
  const [force, setForce] = useState(false);
  useEffect(() => {
    // Wait for inputs are auto filled by browser/passwords save tool, then force label
    const interval = setInterval(() => {
      // used the same selector as in LoginLayout.module.scss
      const autoFilled = document.querySelectorAll("input:-webkit-autofill");

      if (autoFilled.length) {
        setForce(true);
        clearInterval(interval);
      }
    }, 300); // 300ms considered by users as "fast enough"

    return () => clearInterval(interval);
  }, [force]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.center
  }, /*#__PURE__*/React.createElement("h1", {
    className: styles.headerText
  }, "Sign in to DPD"), /*#__PURE__*/React.createElement("form", {
    className: styles.form,
    onSubmit: handleSubmit(onLogin)
  }, /*#__PURE__*/React.createElement(Field, {
    name: "username",
    id: "usernameField",
    type: "text",
    autoComplete: "username",
    label: "Username",
    component: Form.MaterialTextInput,
    maxLength: 100,
    onChange: onInputChange,
    autoFocus: true,
    forceLabel: force
  }), /*#__PURE__*/React.createElement(Field, {
    name: "password",
    id: "passwordField",
    type: "password",
    autoComplete: "password",
    label: "Password",
    component: Form.MaterialTextInput,
    maxLength: 100,
    onPasswordVisibilityToggle: onPasswordToggle,
    onChange: onInputChange,
    forceLabel: force
  }), /*#__PURE__*/React.createElement(Button, {
    className: styles.submitButton,
    id: "submitBtn",
    type: "submit",
    variant: "primary",
    disabled: pristine || submitting || isEmpty(username) || isEmpty(password)
  }, "Sign In")), error && /*#__PURE__*/React.createElement("div", {
    className: styles.error
  }, /*#__PURE__*/React.createElement(FormError, {
    errorMessage: error
  })), isWrongUrlError && /*#__PURE__*/React.createElement("div", {
    className: styles.wrongUrlError
  }, /*#__PURE__*/React.createElement(FormError, {
    errorMessage: M.WRONG_URL
  }), /*#__PURE__*/React.createElement(Button, {
    className: styles.backToLoginButton,
    id: "backToLogin",
    variant: "primary",
    onClick: () => {
      window.location = urlResolver.getUrl("legacy.login");
    }
  }, M.BACK_TO_LOGIN)), /*#__PURE__*/React.createElement("div", {
    className: clsx(styles.divider, {
      [styles.dividerWithError]: !!error
    })
  }), /*#__PURE__*/React.createElement(Link, {
    to: ROUTE.RESET_PASSWORD_PAGE,
    className: styles.changePasswordLink,
    onClick: onChangePassword
  }, "Change your password?", /*#__PURE__*/React.createElement(LinkArrowIcon, {
    className: styles.linkArrow
  })));
};

LoginForm.propTypes = {
  error: PropTypes.string,
  isWrongUrlError: PropTypes.bool,
  handleSubmit: PropTypes.func,
  onLogin: PropTypes.func,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  username: PropTypes.string,
  password: PropTypes.string,
  onPasswordToggle: PropTypes.func,
  onChangePassword: PropTypes.func,
  urlResolver: PropTypes.func,
  onInputChange: PropTypes.func
};
const selector = formValueSelector(FORM.LOGIN);
export default compose(withAppContext, withUrlResolver, withOverlay, withState("isWrongUrlError", "setIsWrongUrlError", false), connect((state, {
  history
}) => ({
  password: selector(state, "password"),
  username: selector(state, "username"),
  error: history.location.state?._error
}), (dispatch, {
  app,
  history,
  location,
  overlay,
  setIsWrongUrlError
}) => ({
  onLogin: overlay.showWhile(async ({
    username = "",
    password = ""
  }) => {
    try {
      const {
        carrier,
        returnUrl
      } = queryString.parse(location.search, {
        parseNumbers: true
      });
      const credentials = AuthHelpers.formatCredentials({
        username,
        password
      }, carrier);
      const resp = await app.auth.signIn(credentials.username, credentials.password); // follow redirects in case of update-password

      if (resp?.changePasswordRequired) {
        history.push({
          pathname: ROUTE.UPDATE_PASSWORD_PAGE,
          search: location.search,
          state: {
            username,
            password
          }
        });
        return;
      }

      await dispatch(AppActions.getAppUser(app.apis));

      if (StringUtil.isAbsoluteHref(returnUrl)) {
        // the current page will not be saved in session
        window.location.replace(returnUrl); // wait for location to be redirected

        return PromiseUtil.postpone(1000);
      } else {
        history.replace({
          pathname: returnUrl || "/"
        });
      }
    } catch (e) {
      AuthErrors.isWrongUrlError(e) && setIsWrongUrlError(true);
      throw new SubmissionError({
        _error: AuthErrors.getUserAuthFormError(e)
      });
    }
  })
})), reduxForm({
  form: FORM.LOGIN
}), withTrack(trackProps(ANALYTICS.LOGON_PAGE)), withTrackProps({
  onPasswordToggle: ANALYTICS.LOGON_PAGE.ON_PASSWORD_TOGGLE,
  onLogin: ANALYTICS.LOGON_PAGE.ON_LOGIN,
  onChangePassword: ANALYTICS.LOGON_PAGE.ON_CLICK_CHANGE_YOUR_PASSWORD,
  onInputChange: (e, prevValues, nextValues, name) => ANALYTICS.LOGON_PAGE.ON_INPUT_CHANGE[name]
}))(LoginForm);